import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { getColors, getShadows } from '../styles/theme';
import {
  createRecurringEvent,
  updateRecurringEvent,
  getRecurringEventById,
} from '../services/database';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

const QUICK_PAIR_GREEN = '#84D288';
const QUICK_PAIR_SHADOW = {
  shadowColor: '#76AF87',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 1,
  shadowRadius: 0,
  elevation: 8,
};

const EditRecurringEventScreen = ({ navigation, route }) => {
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const themeShadows = getShadows(isDarkMode);

  const recurringId = route.params?.recurringId;
  const clubId = route.params?.clubId || appUser?.id;
  const isEditMode = !!recurringId;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventType, setEventType] = useState('CASUAL');
  const [dayOfWeek, setDayOfWeek] = useState(new Date().getDay());
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('21:00');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationLatitude, setLocationLatitude] = useState(null);
  const [locationLongitude, setLocationLongitude] = useState(null);
  const [useClubLocation, setUseClubLocation] = useState(true);
  const [capacity, setCapacity] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchRecurringEvent();
    }
  }, [recurringId]);

  useEffect(() => {
    if (useClubLocation && appUser) {
      setLocationAddress(appUser.address || '');
      setLocationLatitude(appUser.latitude || null);
      setLocationLongitude(appUser.longitude || null);
    }
  }, [useClubLocation, appUser]);

  const fetchRecurringEvent = async () => {
    try {
      setFetching(true);
      const data = await getRecurringEventById(recurringId);
      if (data) {
        setTitle(data.title || '');
        setDescription(data.description || '');
        setEventType(data.event_type || 'CASUAL');
        setDayOfWeek(data.day_of_week);
        setStartTime(data.start_time || '19:00');
        setEndTime(data.end_time || '21:00');
        setLocationName(data.location_name || '');
        setLocationAddress(data.location_address || '');
        setLocationLatitude(data.location_latitude);
        setLocationLongitude(data.location_longitude);
        setCapacity(data.capacity ? String(data.capacity) : '');
        setIsPaid(data.is_paid || false);
        setPrice(data.price ? String(data.price) : '');

        const isClubAddr = appUser?.address && data.location_address === appUser.address;
        setUseClubLocation(isClubAddr);
      }
    } catch (e) {
      console.error('Error fetching recurring event:', e);
      showToast('Failed to load schedule', 'error');
    } finally {
      setFetching(false);
    }
  };

  const formatTimeForDisplay = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const handleSave = async () => {
    if (!title.trim()) {
      showToast('Please enter a title', 'error');
      return;
    }
    if (!startTime) {
      showToast('Please set a start time', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        club_id: clubId,
        title: title.trim(),
        description: description.trim() || null,
        event_type: eventType,
        day_of_week: dayOfWeek,
        start_time: startTime,
        end_time: endTime || null,
        location_name: useClubLocation ? (appUser?.clubName || '') : locationName.trim(),
        location_address: useClubLocation ? (appUser?.address || '') : locationAddress.trim(),
        location_latitude: useClubLocation ? appUser?.latitude : locationLatitude,
        location_longitude: useClubLocation ? appUser?.longitude : locationLongitude,
        capacity: capacity ? parseInt(capacity, 10) : null,
        is_paid: isPaid,
        price: isPaid && price ? parseFloat(price) : null,
      };

      if (isEditMode) {
        delete payload.club_id;
        await updateRecurringEvent(recurringId, payload);
        showToast('Schedule updated!', 'success');
      } else {
        await createRecurringEvent(payload);
        showToast('Recurring schedule created!', 'success');
      }
      navigation.goBack();
    } catch (e) {
      console.error('Error saving recurring event:', e);
      showToast('Failed to save schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.borderLight }, themeShadows.small]}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={[styles.backButton, { backgroundColor: colors.background + (isDarkMode ? 'AA' : '99') }]}
        >
          <Icon name="arrow-back" size={24} color={colors.text} />
          <Text style={[styles.backText, { color: colors.text }]}>Back</Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>
          {isEditMode ? 'Edit Recurring Schedule' : 'New Recurring Schedule'}
        </Text>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} showsVerticalScrollIndicator={false}>
        {/* Title */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Event Title</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={title}
            onChangeText={setTitle}
            placeholder="e.g., Weekly Blitz Night"
            placeholderTextColor={colors.textLight}
          />
        </View>

        {/* Day of Week */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Day of Week</Text>
          <View style={styles.dayGrid}>
            {DAYS_OF_WEEK.map((day) => (
              <TouchableOpacity
                key={day.value}
                style={[
                  styles.dayButton,
                  { backgroundColor: colors.surface, borderColor: colors.borderLight },
                  dayOfWeek === day.value && [styles.dayButtonActive, { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
                ]}
                onPress={() => setDayOfWeek(day.value)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.dayButtonText,
                    { color: colors.textSecondary },
                    dayOfWeek === day.value && { color: '#fff', fontWeight: '700' },
                  ]}
                >
                  {day.short}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Start / End Time */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Time</Text>
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.subLabel, { color: colors.textSecondary }]}>Start</Text>
              {Platform.OS === 'web' ? (
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{
                    padding: 12,
                    fontSize: 16,
                    borderRadius: 8,
                    border: `1px solid ${colors.borderLight}`,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <TextInput
                  style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
                  value={formatTimeForDisplay(startTime)}
                  placeholder="19:00"
                  placeholderTextColor={colors.textLight}
                  editable={false}
                />
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.subLabel, { color: colors.textSecondary }]}>End</Text>
              {Platform.OS === 'web' ? (
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{
                    padding: 12,
                    fontSize: 16,
                    borderRadius: 8,
                    border: `1px solid ${colors.borderLight}`,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <TextInput
                  style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
                  value={formatTimeForDisplay(endTime)}
                  placeholder="21:00"
                  placeholderTextColor={colors.textLight}
                  editable={false}
                />
              )}
            </View>
          </View>
        </View>

        {/* Event Type */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Event Type</Text>
          <View style={styles.eventTypeContainer}>
            {['CASUAL', 'BLITZ', 'TOURNAMENT', 'CLASS'].map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.eventTypeButton,
                  { backgroundColor: colors.surface, borderColor: colors.borderLight },
                  eventType === type && [styles.eventTypeButtonActive, { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
                ]}
                onPress={() => setEventType(type)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.eventTypeText,
                    { color: colors.textSecondary },
                    eventType === type && { color: '#fff' },
                  ]}
                >
                  {type.charAt(0) + type.slice(1).toLowerCase()}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Location */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Location</Text>
          <TouchableOpacity
            style={styles.locationToggle}
            onPress={() => setUseClubLocation(!useClubLocation)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, { borderColor: colors.primary }]}>
              {useClubLocation && <Icon name="check" size={18} color={colors.primary} />}
            </View>
            <Text style={[styles.locationToggleText, { color: colors.text }]}>Use Club Location</Text>
          </TouchableOpacity>
          {!useClubLocation && (
            <>
              <TextInput
                style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text, marginBottom: 8 }]}
                value={locationName}
                onChangeText={setLocationName}
                placeholder="Location name (e.g., Central Park Pavilion)"
                placeholderTextColor={colors.textLight}
              />
              <TextInput
                style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
                value={locationAddress}
                onChangeText={setLocationAddress}
                placeholder="Address"
                placeholderTextColor={colors.textLight}
                multiline
              />
              <TouchableOpacity
                style={[styles.mapPickerButton, { borderColor: colors.primary, backgroundColor: colors.primary + '18' }]}
                onPress={() => {
                  navigation.navigate('MapPicker', {
                    onLocationSelect: (coords, address) => {
                      setLocationLatitude(coords.latitude);
                      setLocationLongitude(coords.longitude);
                      if (address) setLocationAddress(address);
                    },
                  });
                }}
              >
                <Icon name="map" size={20} color={colors.primary} />
                <Text style={[styles.mapPickerButtonText, { color: colors.text }]}>Pick on Map</Text>
              </TouchableOpacity>
            </>
          )}
          {useClubLocation && appUser?.address && (
            <Text style={[styles.locationPreview, { color: colors.textSecondary }]}>{appUser.address}</Text>
          )}
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Description (optional)</Text>
          <TextInput
            style={[styles.input, styles.textArea, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={description}
            onChangeText={setDescription}
            placeholder="Weekly meetup details, rules, what to bring..."
            placeholderTextColor={colors.textLight}
            multiline
            numberOfLines={3}
          />
        </View>

        {/* Capacity */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: colors.text }]}>Capacity (optional)</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.surface, borderColor: colors.borderLight, color: colors.text }]}
            value={capacity}
            onChangeText={setCapacity}
            placeholder="Maximum attendees"
            placeholderTextColor={colors.textLight}
            keyboardType="number-pad"
          />
        </View>

        {/* Paid */}
        <View style={styles.section}>
          <View style={styles.paymentTypeContainer}>
            <TouchableOpacity
              style={[
                styles.paymentTypeButton,
                { backgroundColor: colors.surface, borderColor: colors.borderLight },
                !isPaid && [{ backgroundColor: QUICK_PAIR_GREEN + '22', borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
              ]}
              onPress={() => { setIsPaid(false); setPrice(''); }}
              activeOpacity={0.7}
            >
              <Icon name="check-circle" size={20} color={!isPaid ? colors.primary : colors.textLight} />
              <Text style={[styles.paymentTypeText, { color: colors.textSecondary }, !isPaid && { color: colors.text }]}>
                Free
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.paymentTypeButton,
                { backgroundColor: colors.surface, borderColor: colors.borderLight },
                isPaid && [{ backgroundColor: QUICK_PAIR_GREEN + '22', borderColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW],
              ]}
              onPress={() => setIsPaid(true)}
              activeOpacity={0.7}
            >
              <Icon name="attach-money" size={20} color={isPaid ? colors.primary : colors.textLight} />
              <Text style={[styles.paymentTypeText, { color: colors.textSecondary }, isPaid && { color: colors.text }]}>
                Paid
              </Text>
            </TouchableOpacity>
          </View>
          {isPaid && (
            <View style={[styles.priceInputContainer, { backgroundColor: colors.surface, borderColor: colors.borderLight }]}>
              <View style={[styles.priceInputWrapper, { borderColor: colors.borderLight, backgroundColor: colors.background }]}>
                <Text style={[styles.currencySymbol, { color: colors.text }]}>$</Text>
                <TextInput
                  style={[styles.priceInput, { color: colors.text }]}
                  value={price}
                  onChangeText={setPrice}
                  placeholder="0.00"
                  placeholderTextColor={colors.textLight}
                  keyboardType="decimal-pad"
                />
              </View>
            </View>
          )}
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, { backgroundColor: QUICK_PAIR_GREEN }, QUICK_PAIR_SHADOW, loading && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={loading}
          activeOpacity={0.8}
        >
          {loading ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <Text style={styles.saveButtonText}>
              {isEditMode ? 'Update Schedule' : 'Create Recurring Schedule'}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
  backText: {
    fontSize: 16,
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  subLabel: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  dayGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dayButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 48,
    alignItems: 'center',
  },
  dayButtonActive: {
    borderWidth: 1,
  },
  dayButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  eventTypeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  eventTypeButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  eventTypeButtonActive: {},
  eventTypeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  locationToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderRadius: 4,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationToggleText: {
    fontSize: 16,
  },
  locationPreview: {
    fontSize: 14,
    marginTop: 8,
    fontStyle: 'italic',
  },
  mapPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  mapPickerButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  paymentTypeContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  paymentTypeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 8,
    borderWidth: 2,
  },
  paymentTypeText: {
    fontSize: 16,
    fontWeight: '600',
  },
  priceInputContainer: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  priceInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: 'bold',
    marginRight: 8,
  },
  priceInput: {
    flex: 1,
    padding: 12,
    fontSize: 24,
    fontWeight: 'bold',
  },
  saveButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
});

export default EditRecurringEventScreen;
